import { ReactElement } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  BulletedListIcon,
  ClockIcon,
  ConfirmIcon,
  FinalMilestoneIcon,
  QuizIcon,
  RefreshIcon,
  ReviewContentIcon,
  RightArrowIcon,
  StudentIcon,
} from 'src/v3/core/icons'
import {
  CourseV2Content,
  CourseV2Progress,
  CourseV2Quiz,
} from 'src/v3/classroom-v2/types'

// Passing score is a COUNT of correct answers, not a percentage — the API
// computes Passed as (correctAnswers.length >= quiz.passingScore) in
// Event/QuizSession.fs. Rendering it as a percentage (as the original design
// mock did) would misstate the requirement, so every surface here phrases it
// as "N of M correct", matching quiz-preview.tsx and field-approval.tsx.
let passingScoreText = (quiz: CourseV2Quiz) =>
  `${quiz.passingScore} of ${quiz.displayedQuestions} correct`

let formatCEHours = (length: number) =>
  Number.isInteger(length) ? length.toFixed(1) : String(length)

// Every other value here is short by nature — a count ("2/2") or a rounded
// hour figure ("1.0") — so this is really "is this a category name, not a
// measurement" in disguise, without StatTile needing to know what a category
// is. Chosen well above the longest of those (5 characters) and well below a
// short category name, so neither renders as the other.
let COMPACT_VALUE_THRESHOLD = 10

// Breaks a slash-delimited value (a course category like
// "Airway/Respiratory/Ventilation") only at its slashes, by inserting a
// <wbr> after each one — a real wrap opportunity the browser prefers over
// the overflowWrap:'anywhere' fallback below, which otherwise breaks mid-word
// wherever the line happens to run out of room ("Res-piratory"). The slash
// itself stays on the line it started, so the value still reads as one
// path-like string rather than acquiring a hyphen it never had.
let withSlashBreaks = (value: string): ReactElement[] =>
  value.split('/').map((part, i, parts) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <>
          /<wbr />
        </>
      )}
    </span>
  ))

let StatTile = ({
  icon,
  value,
  label,
  valueColor,
}: {
  icon: ReactElement
  value: string
  label: string
  valueColor?: string
}): ReactElement => {
  // A category name is a label, not a measurement — the same giant bold
  // digits that suit "3/5" or "1.0" read as shouting once the value is a
  // sentence-length string, and at that size a stacked mobile tile still
  // can't fit more than a word or two per line. Long values get a smaller,
  // regular-weight style sized for prose instead, decided from the value
  // itself so any future long stat gets this for free rather than needing a
  // caller to remember to ask for it.
  let isCompact = value.length > COMPACT_VALUE_THRESHOLD

  return (
    // minWidth: 0 lets this shrink below its content's intrinsic width — the
    // default flex-item min-width is auto, which otherwise holds an unbroken
    // value (like a long course category) at full width and forces the whole
    // stats row wider than the viewport on mobile instead of wrapping it.
    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', px: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          mb: 0.5,
        }}
      >
        {icon}
        <Typography
          variant={isCompact ? 'body1' : 'h2'}
          sx={{
            fontSize: isCompact ? '1rem' : '1.75rem',
            fontWeight: isCompact ? 'fontWeightMedium' : undefined,
            // The theme's h2 line-height is a fixed 72px, sized for the
            // unscaled default font size — inherited as-is here since only
            // fontSize is overridden, it would space wrapped lines of a long
            // value (e.g. a course category tile on mobile) 72px apart. 1.2
            // scales with the overridden size instead.
            lineHeight: 1.2,
            color: valueColor,
            overflowWrap: 'anywhere',
          }}
        >
          {isCompact ? withSlashBreaks(value) : value}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

let OverviewRow = ({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: ReactElement
  label: string
  value: string
  valueColor?: string
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.25,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon}
      <Typography variant="body1">{label}</Typography>
    </Box>
    <Typography
      variant="body1"
      sx={{ color: valueColor ?? 'text.primary', fontWeight: 500 }}
    >
      {value}
    </Typography>
  </Box>
)

// Why the survey matters, stated before the quiz rather than discovered after
// it. When the course requires a survey (the backend decides this from CAPCE /
// Accredited accreditation, so it's known up front — see
// getSurveyRequirementAndSubmission), passing the quiz alone earns nothing:
// both the CE submission and the certificate are withheld until the survey is
// submitted. A student who closes the tab on a passing score would otherwise
// believe they were done.
let SurveyRequirementNotice = (): ReactElement => (
  <Alert
    severity="info"
    icon={<StudentIcon fontSize="small" />}
    sx={{ mt: 3, textAlign: 'left' }}
  >
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
      A short survey follows this assessment
    </Typography>
    <Typography variant="body2" color="text.secondary">
      After you pass, completing the post-quiz survey is required before your CE
      hours can be submitted and your certificate issued.
    </Typography>
  </Alert>
)

export type PreQuizStepProps = {
  content: CourseV2Content
  quiz: CourseV2Quiz
  progress: CourseV2Progress
  isStarting: boolean
  hasStartFailed: boolean
  onStartQuiz: () => void
  onReviewContent: () => void
}

export let PreQuizStep = ({
  content,
  quiz,
  progress,
  isStarting,
  hasStartFailed,
  onStartQuiz,
  onReviewContent,
}: PreQuizStepProps): ReactElement => {
  let theme = useTheme()
  let isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // A prior failed attempt reframes the whole screen: the user already knows
  // what an assessment is, so the useful information is what they scored, what
  // they need, and that another attempt costs them nothing.
  let priorAttempt = progress.quiz.kind === 'failed' ? progress.quiz : null
  let completedStepCount = progress.steps.filter((s) => s.completed).length
  let isSurveyRequired = progress.survey.kind !== 'notRequired'

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Card
        variant="outlined"
        sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}
      >
        <FinalMilestoneIcon
          sx={{ fontSize: 40, color: 'primary.main', mb: 2 }}
        />
        <Typography
          variant="h1"
          // The theme's h1 line-height is a fixed 112px, sized for the
          // unscaled default font size — inherited as-is here since only
          // fontSize is overridden, it would space this title's two lines
          // 112px apart the moment it wraps on a narrow viewport instead of
          // sitting close together like normal heading text.
          sx={{ fontSize: '2rem', lineHeight: 1.25, mb: 1.5 }}
        >
          {priorAttempt
            ? 'Ready to retake your assessment?'
            : 'Ready for your assessment?'}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 520, mx: 'auto' }}
        >
          {priorAttempt
            ? `You scored ${priorAttempt.score} of ${quiz.displayedQuestions}. You need ${priorAttempt.passingScore} of ${quiz.displayedQuestions} correct to pass. Retakes are unlimited.`
            : "You've completed all course content. Demonstrate your knowledge to earn your CE certificate."}
        </Typography>

        {/* The CTA and its survey caveat sit right under the title —
            everything below (the stats row, the assessment overview) is
            supporting detail for someone who scrolls to it, not something
            that should stand between "ready?" and the button that answers
            it. On a short viewport the button used to land below the fold,
            behind two cards' worth of detail the learner hadn't asked for
            yet. */}
        {isSurveyRequired && <SurveyRequirementNotice />}

        {hasStartFailed && (
          <Alert severity="error" sx={{ mt: 3, textAlign: 'left' }}>
            Something went wrong starting your assessment. Please try again.
          </Alert>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          endIcon={<RightArrowIcon />}
          onClick={onStartQuiz}
          disabled={isStarting}
          sx={{ mt: 4, py: 1.5, borderRadius: 8 }}
        >
          {isStarting
            ? 'Starting…'
            : priorAttempt
              ? 'Retake Assessment'
              : 'Start Assessment'}
        </Button>

        <Button
          startIcon={<ReviewContentIcon fontSize="small" />}
          onClick={onReviewContent}
          sx={{ mt: 1.5, color: 'text.secondary' }}
        >
          Review course content first
        </Button>

        {/* Stacked on mobile rather than shrunk to fit: the third tile's
            value is a course category, an arbitrary-length string with no
            natural break points, and three tiles side by side on a
            360-420px viewport leaves it no width that both fits the text
            and stays legible. Stacking gives each tile the full row instead.
            The divider follows — 'vertical' assumes a side-by-side layout it
            no longer has once stacked. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 2, sm: 0 },
            mt: 4,
            py: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paperDark',
          }}
        >
          <StatTile
            icon={<ConfirmIcon sx={{ fontSize: 20, color: 'success.main' }} />}
            value={`${completedStepCount}/${content.steps.length}`}
            label="Steps Complete"
          />
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem={!isMobile}
          />
          <StatTile
            icon={<ClockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
            value={formatCEHours(content.length)}
            label={content.length === 1 ? 'CE Hour' : 'CE Hours'}
          />
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem={!isMobile}
          />
          <StatTile
            icon={
              <StudentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            }
            value={content.category}
            label="Course"
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'left',
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1, display: 'block', mb: 1 }}
          >
            Assessment Overview
          </Typography>
          <OverviewRow
            icon={<QuizIcon fontSize="small" color="disabled" />}
            label="Questions"
            value={`${quiz.displayedQuestions} questions`}
          />
          <Divider />
          <OverviewRow
            icon={
              <FinalMilestoneIcon
                fontSize="small"
                sx={{ color: 'warning.main' }}
              />
            }
            label="Passing score"
            value={passingScoreText(quiz)}
            valueColor={theme.palette.warning.main}
          />
          <Divider />
          <OverviewRow
            icon={<BulletedListIcon fontSize="small" color="disabled" />}
            label="Format"
            value="Multiple choice"
          />
          <Divider />
          {/* Unlimited is a property of the domain, not a configured field:
              a passing session closes and the next create-session call starts
              fresh, with no attempt cap anywhere in the model. There is
              deliberately no "Time limit" row — no such concept exists, and
              rendering "No limit" would imply a feature that doesn't. */}
          <OverviewRow
            icon={<RefreshIcon fontSize="small" color="disabled" />}
            label="Retakes"
            value="Unlimited"
          />
        </Box>
      </Card>
    </Box>
  )
}
